import React, {useEffect, useMemo, useState} from 'react';
import { useCompressorPackingSummary } from '../../../hooks/PackingHealth/useCompressorPackingSummary';
import { useParams } from 'react-router-dom';
import ThrowPackingTempsLineChart from "./PackingTempsLineChart/ThrowPackingTempsLineChart";
import './packingsTab.scss';
import ThrowPackingCard from "./ThrowPackingCard/ThrowPackingCard";
import {Skeleton, Typography} from "@mui/material";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
    FramePackingHistoricalData,
    PackingGraphDataPoint,
} from "../../../autogenerate/api.generated.clients";
import ValueNotReceivedMessage from "../../../components/ValueNotReceivedMessage/ValueNotReceivedMessage";


function PackingsTab() {
    const params = useParams();
    const frameSerialNumber = params.frameSerialNumber;

    const [packingData, setPackingData] = useState<FramePackingHistoricalData | null>(null);
    const [endDate, setEndDate] = useState(useMemo(() => new Date(), []));
    const [startDate, setStartDate] = useState(useMemo(() => {
        const date = new Date();
        date.setDate(endDate.getDate() - 7);
        return date;
    }, [endDate]));
    const [datePickerStartDate, setDatePickerStartDate] = useState<Date | undefined>();
    const [datePickerEndDate, setDatePickerEndDate] = useState<Date | undefined>();


    const compressorPackingSummary = useCompressorPackingSummary({ frameSerialNumber, startDate, endDate });

    useEffect(() => {
        setDatePickerStartDate(startDate);
        setDatePickerEndDate(endDate);
    }, [startDate, endDate]);

    useEffect(() => {
        if(compressorPackingSummary.data) {
            setPackingData(compressorPackingSummary.data);
        }
    }, [compressorPackingSummary.data]);
    
    const hasData = compressorPackingSummary.data && compressorPackingSummary.data.throwPackingHistoricalData?.length > 0;
    
    
    const handleDateChange = (startDateUpdate: Date, endDateUpdate: Date) => {
        const isStartDateBetweenCurrentDates = startDateUpdate >= startDate && startDateUpdate <= endDate;
        const isEndDateBetweenCurrentDates = endDateUpdate >= startDate && endDateUpdate <= endDate;

        if (isStartDateBetweenCurrentDates && isEndDateBetweenCurrentDates) {
            const filteredPackingData = compressorPackingSummary.data!.throwPackingHistoricalData.map(throwData => ({
                ...throwData,
                packingVentTemperatures: filterData(throwData.packingVentTemperatures, startDateUpdate, endDateUpdate)}));
            setDatePickerStartDate(startDateUpdate);
            setDatePickerEndDate(endDateUpdate);
            setPackingData({...packingData!, throwPackingHistoricalData: filteredPackingData});
        } else {
            setStartDate(startDateUpdate);
            setEndDate(endDateUpdate);
        }
    }
    
    if(compressorPackingSummary.isLoading) {
        return (
            <div className={'packings-tab-container'}>
                <Skeleton variant={'rectangular'} width={'33%'} height={40}/>
                <div className={'e-card'} style={{height: '400px'}}>
                    <div className={'card-container'} style={{gap: '1em'}}>
                        <Skeleton variant="rectangular" height={'75%'} width={'30%'} style={{marginTop: '3em'}}/>
                        <Skeleton variant="rectangular" height={'75%'} width={'60%'} style={{marginTop: '3em'}}/>
                    </div>
                </div>
                <div className={'e-card'} style={{height: '400px'}}>
                    <div className={'card-container'} style={{gap: '1em'}}>
                        <Skeleton variant="rectangular" height={'75%'} width={'30%'} style={{marginTop: '3em'}}/>
                        <Skeleton variant="rectangular" height={'75%'} width={'60%'} style={{marginTop: '3em'}}/>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={'packings-tab-container'}>
            <div style={{display: 'flex', flexDirection: 'row', gap: '1em'}}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker label={'Start Date'} value={dayjs(datePickerStartDate)} onChange={(value) => {value && handleDateChange(value.toDate(), endDate)}}/>
                    <DatePicker label={'End Date'} value={dayjs(datePickerEndDate)} onChange={(value) => {value && handleDateChange(startDate, value.toDate())}}/>
                </LocalizationProvider>
            </div>
            {!compressorPackingSummary.isLoading && !hasData &&
                <div className="packings-value-not-received">
                <ValueNotReceivedMessage />
                </div>
            }
            
            {!compressorPackingSummary.isLoading && hasData &&
                packingData?.throwPackingHistoricalData?.map((data, index) => {
                    return (
                        <div key={index} className={'e-card'} style={{height: '400px'}}>
                            <Typography variant={'h5'} className={'card-title'}>Throw {data.throwNumber}</Typography>
                            <div className={'card-container'}>
                                <div className={'card-content one-third-width'} >
                                    <Typography variant={'body1'} className={'card-subtitle'} >Current Packing Leakage Likelihood</Typography>
                                    <ThrowPackingCard data={data.prediction} isLoading={compressorPackingSummary.isLoading} />
                                </div>
                                <div className={'card-content two-third-width'}>
                                    <Typography variant={'body1'} className={'card-subtitle'} >Packing Vent Temperature Readings</Typography>
                                    <ThrowPackingTempsLineChart data={data} isLoading={compressorPackingSummary.isLoading} />
                                </div>
                            </div>
                        </div>
                    );
                })
            }
        </div>
    );
}

export function filterData(data: PackingGraphDataPoint[], startDate: Date, endDate: Date){
    return data.filter(temp => {
        const tempDate : Date = new Date(temp.x);
        return tempDate >= startDate && tempDate <= endDate;
    });
}

export default PackingsTab;